/**
 * Stock — what is where, what is short, and putting a wrong count right.
 *
 * Three questions, in the order somebody actually asks them:
 *
 *   1. What is on the vans and in the store, and what is it worth? The answer
 *      to "why is our stock bill so high" is usually sitting in three vans.
 *   2. What do I need to order? Measured on *available* — four panels on the
 *      shelf with four promised to Monday's install is not four panels.
 *   3. This shelf says four and the screen says six.
 *
 * The third one is why this screen is built to work on a phone. A stocktake
 * happens standing in front of the shelf, and a stock screen that only works
 * at a desk is a stocktake that gets written on paper and typed in on Friday,
 * which is to say a stocktake that does not happen.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { money, moneyShort, qty as fmtQty, titleCase } from "../lib/format";
import { Card, Chip, DataTable, Empty, ErrorState, Loading, PageHeader, Tile, Tiles } from "../components/ui";
import type { StockLevelOut, StockLocationOut } from "../lib/types";

type Tab = "holdings" | "order" | "count";

export default function Stock() {
  const { can } = useAuth();
  const isOffice = can(...OFFICE);

  const [tab, setTab] = useState<Tab>("holdings");
  const [where, setWhere] = useState<string>("");

  const locations = useQuery({ queryKey: ["stock", "locations"], queryFn: api.stock.locations });
  const shortfalls = useQuery({ queryKey: ["stock", "shortfalls"], queryFn: api.stock.shortfalls });

  if (locations.isPending) return <Loading what="Loading stock" />;
  if (locations.error) return <ErrorState error={locations.error} onRetry={() => void locations.refetch()} />;

  const held = locations.data;
  const totalValue = held.reduce((sum, l) => sum + l.value_cents, 0);
  const onVans = held.filter((l) => l.kind === "van").reduce((sum, l) => sum + l.value_cents, 0);
  const short = shortfalls.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Stock">
        {held.length} location{held.length === 1 ? "" : "s"}. Levels come from the movement ledger, so a
        wrong count is a correction rather than an argument.
      </PageHeader>

      <Tiles>
        <Tile label="Held" value={moneyShort(totalValue)} detail="at cost, everywhere" />
        <Tile
          label="On vans"
          value={moneyShort(onVans)}
          detail={`${Math.round((onVans / Math.max(totalValue, 1)) * 100)}% of it`}
          tone={onVans > totalValue / 2 ? "warn" : undefined}
        />
        <Tile
          label="Below reorder"
          value={short.length}
          detail={short[0]?.description ?? "nothing to order"}
          tone={short.length ? "warn" : "ok"}
        />
      </Tiles>

      <div className="segs" role="tablist">
        {(
          [
            ["holdings", "Holdings"],
            ["order", `To order${short.length ? ` (${short.length})` : ""}`],
            ["count", "Stocktake"],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            className={`seg${tab === key ? " seg-on" : ""}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "holdings" && <Holdings locations={held} where={where} onWhere={setWhere} />}
      {tab === "order" && <ToOrder rows={short} pending={shortfalls.isPending} />}
      {tab === "count" && <Stocktake locations={held} isOffice={isOffice} />}
    </div>
  );
}

/* -------------------------------------------------------------- holdings */

function Holdings({
  locations,
  where,
  onWhere,
}: {
  locations: StockLocationOut[];
  where: string;
  onWhere: (value: string) => void;
}) {
  const levels = useQuery({
    queryKey: ["stock", "levels", where],
    queryFn: () => api.stock.levels(where ? { location_id: where } : {}),
  });

  return (
    <>
      <div className="split">
        <div className="split-main">
          <Card
            title={where ? locations.find((l) => l.id === where)?.name ?? "Holdings" : "Everything held"}
            subtitle={levels.data ? `${levels.data.length} lines` : undefined}
            padded={false}
          >
            {levels.isPending ? (
              <Loading what="Loading levels" />
            ) : levels.data && levels.data.length > 0 ? (
              <DataTable
                columns={[
                  { key: "item", label: "Item" },
                  { key: "where", label: "Where", hideOnPhone: true },
                  { key: "qty", label: "On hand", align: "right" },
                  { key: "avail", label: "Available", align: "right" },
                  { key: "value", label: "Value", align: "right", hideOnPhone: true },
                ]}
              >
                {levels.data.map((row) => (
                  <tr key={`${row.location_id}-${row.catalogue_item_id}`}>
                    <td>
                      {row.description}
                      {row.qty < 0 && (
                        <>
                          {" "}
                          <Chip tone="crit">count is wrong</Chip>
                        </>
                      )}
                    </td>
                    <td className="phone-hide muted">{row.location_name}</td>
                    <td className="right mono">
                      {fmtQty(row.qty)} {row.uom}
                    </td>
                    <td className="right mono">
                      {/* Available, not on hand: what is already promised to
                          accepted work cannot be promised again. */}
                      {fmtQty(row.available_qty)}
                      {row.committed_qty > 0 && (
                        <span className="tiny muted"> · {fmtQty(row.committed_qty)} held</span>
                      )}
                    </td>
                    <td className="right mono phone-hide">{money(row.value_cents)}</td>
                  </tr>
                ))}
              </DataTable>
            ) : (
              <Empty title="Nothing held here">
                Stock arrives by receiving a purchase order, or by transferring it from another
                location.
              </Empty>
            )}
          </Card>
        </div>

        <aside className="split-side">
          <Card title="Locations">
            <div className="btn-row" style={{ flexWrap: "wrap", marginBottom: 10 }}>
              <button
                className={`btn${where === "" ? " btn-primary" : ""}`}
                type="button"
                onClick={() => onWhere("")}
              >
                All
              </button>
              {locations.map((location) => (
                <button
                  key={location.id}
                  className={`btn${where === location.id ? " btn-primary" : ""}`}
                  type="button"
                  onClick={() => onWhere(location.id)}
                >
                  {location.name}
                </button>
              ))}
            </div>
            <dl className="kv kv-money">
              {locations.map((location) => (
                <div key={location.id} style={{ display: "contents" }}>
                  <dt>
                    {location.name}
                    {/* "Seacliff Park store Store" — only say the kind when
                        the name has not already said it. */}
                    {!location.name.toLowerCase().includes(location.kind) && (
                      <span className="tiny muted"> {titleCase(location.kind)}</span>
                    )}
                  </dt>
                  <dd className="mono">{money(location.value_cents)}</dd>
                </div>
              ))}
            </dl>
          </Card>
        </aside>
      </div>
    </>
  );
}

/* -------------------------------------------------------------- to order */

function ToOrder({
  rows,
  pending,
}: {
  rows: {
    catalogue_item_id: string;
    description: string;
    location_name: string;
    on_hand: number;
    committed: number;
    available: number;
    reorder_point: number;
    suggested_qty: number;
  }[];
  pending: boolean;
}) {
  if (pending) return <Loading what="Working out what is short" />;
  if (rows.length === 0) {
    return (
      <Card title="To order">
        <Empty title="Nothing is below its reorder point">
          Items with no reorder point are never listed here — not everything is worth tracking to a
          minimum, and silence is the default.
        </Empty>
      </Card>
    );
  }

  return (
    <Card
      title="To order"
      subtitle="Worst first. Measured on available, not on the shelf count."
      padded={false}
    >
      <DataTable
        columns={[
          { key: "item", label: "Item" },
          { key: "where", label: "Where", hideOnPhone: true },
          { key: "avail", label: "Available", align: "right" },
          { key: "point", label: "Reorder at", align: "right", hideOnPhone: true },
          { key: "order", label: "Order", align: "right" },
        ]}
      >
        {rows.map((row) => (
          <tr key={`${row.location_name}-${row.catalogue_item_id}`}>
            <td>{row.description}</td>
            <td className="phone-hide muted">{row.location_name}</td>
            <td className="right mono">
              {fmtQty(row.available)}
              {row.committed > 0 && (
                <span className="tiny muted">
                  {" "}
                  · {fmtQty(row.on_hand)} on hand, {fmtQty(row.committed)} promised
                </span>
              )}
            </td>
            <td className="right mono phone-hide muted">{fmtQty(row.reorder_point)}</td>
            <td className="right mono">
              <b>{fmtQty(row.suggested_qty)}</b>
            </td>
          </tr>
        ))}
      </DataTable>
    </Card>
  );
}

/* ------------------------------------------------------------- stocktake */

/**
 * Counting a shelf.
 *
 * Every line starts blank rather than pre-filled with what the system believes.
 * A pre-filled count is a count that gets confirmed rather than made, which is
 * the entire failure mode of stocktakes — and the system's number is right
 * there in the next column for comparison afterwards.
 */
function Stocktake({ locations, isOffice }: { locations: StockLocationOut[]; isOffice: boolean }) {
  const queryClient = useQueryClient();
  const [where, setWhere] = useState<string>(locations[0]?.id ?? "");
  const [counted, setCounted] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<{ changed: number; warnings: string[] } | null>(null);

  const levels = useQuery({
    queryKey: ["stock", "levels", where, "all"],
    queryFn: () => api.stock.levels({ location_id: where, include_empty: true }),
    enabled: Boolean(where),
  });

  const submit = useMutation({
    mutationFn: (lines: { catalogue_item_id: string; location_id: string; counted: number }[]) =>
      api.stock.count(lines),
    onSuccess: (results) => {
      setDone({
        changed: results.length,
        warnings: results.flatMap((r) => r.warnings),
      });
      setCounted({});
      void queryClient.invalidateQueries({ queryKey: ["stock"] });
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't record the count."),
  });

  const lines = useMemo(() => {
    return Object.entries(counted)
      .filter(([, value]) => value.trim() !== "" && Number.isFinite(Number(value)))
      .map(([itemId, value]) => ({
        catalogue_item_id: itemId,
        location_id: where,
        counted: Number(value),
      }));
  }, [counted, where]);

  return (
    <Card
      title="Stocktake"
      subtitle="Type what is actually there. Only the lines you fill in are recorded."
    >
      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}

      {done && (
        <div className="notice notice-info" role="status">
          {done.changed === 0 ? (
            <>Everything you counted already matched. Nothing was recorded, which is the right answer.</>
          ) : (
            <>
              <b>
                {done.changed} correction{done.changed === 1 ? "" : "s"} recorded.
              </b>{" "}
              Each one is stored as the difference, so the ledger still explains the level.
            </>
          )}
          {done.warnings.map((w, i) => (
            <div key={i} className="tiny">
              {w}
            </div>
          ))}
        </div>
      )}

      <div className="btn-row" style={{ flexWrap: "wrap", marginBottom: 12 }}>
        {locations.map((location) => (
          <button
            key={location.id}
            className={`btn${where === location.id ? " btn-primary" : ""}`}
            type="button"
            onClick={() => {
              setWhere(location.id);
              setCounted({});
              setDone(null);
            }}
          >
            {location.name}
          </button>
        ))}
      </div>

      {levels.isPending ? (
        <Loading what="Loading what should be here" />
      ) : (
        <DataTable
          columns={[
            { key: "item", label: "Item" },
            { key: "system", label: "System says", align: "right" },
            { key: "counted", label: "Counted", align: "right" },
          ]}
        >
          {(levels.data ?? []).map((row: StockLevelOut) => (
            <tr key={row.catalogue_item_id}>
              <td>{row.description}</td>
              <td className="right mono muted">
                {fmtQty(row.qty)} {row.uom}
              </td>
              <td className="right">
                <input
                  className="count-input"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  value={counted[row.catalogue_item_id] ?? ""}
                  placeholder="—"
                  aria-label={`Counted quantity of ${row.description}`}
                  onChange={(e) =>
                    setCounted((prev) => ({ ...prev, [row.catalogue_item_id]: e.target.value }))
                  }
                />
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <button
        className="btn btn-primary btn-block"
        type="button"
        style={{ marginTop: 14 }}
        disabled={lines.length === 0 || submit.isPending}
        onClick={() => {
          setProblem(null);
          setDone(null);
          submit.mutate(lines);
        }}
      >
        {submit.isPending
          ? "Recording…"
          : lines.length === 0
            ? "Fill in what you counted"
            : `Record ${lines.length} count${lines.length === 1 ? "" : "s"}`}
      </button>

      {isOffice && (
        <p className="tiny muted" style={{ marginTop: 10 }}>
          Counts are stored as the difference from what the system believed, never as a new total — so
          the movement ledger still adds up to the level afterwards, and it says who changed what.
        </p>
      )}
    </Card>
  );
}
